'use client';

import { type FC } from 'react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  useCarousel,
} from '@wwe-portal/ui/components/carousel';
import { getYoutubeVideoId, getYoutubeEmbedLink } from '@wwe-portal/utils/link';
import { ClickableDiv } from '@wwe-portal/ui/components/button';
import Image from 'next/image';
import YoutubePlayIcon from '@wwe-portal/ui/public/assets/images/common/youtube-play.svg';
import { YoutubeDialog } from '@wwe-portal/ui/components/dialog/youtube-dialog';
import { showDialog } from '@wwe-portal/ui/components/dialog';
import { cloneArrayWithId } from '@wwe-portal/utils/array';
import { AspectRatio } from '@wwe-portal/ui/components/aspect-ratio';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';

export interface RosterPlaylistProps {
  videoLinks?: {
    image: string;
    link: string;
  }[];
}

export const RosterPlaylist: FC<RosterPlaylistProps> = ({ videoLinks }) => {
  return (
    <AspectRatio ratio={192 / 108} className="relative h-full w-full">
      <Carousel
        className="h-full w-full overflow-hidden"
        opts={{ loop: true, containScroll: 'trimSnaps' }}
      >
        <CarouselContent
          containerClassName="h-full w-full"
          className="relative ml-0 h-full w-full overflow-visible"
        >
          {videoLinks?.length ? (
            cloneArrayWithId(videoLinks).map((videoLink) => {
              const embededVideoUrl = getYoutubeEmbedLink(
                videoLink.link.trim() ?? ''
              );
              const videoThumbnailUrl = videoLink.image.trim() ?? '';

              return (
                <CarouselItem
                  key={videoLink.id}
                  className="relative h-full w-full pl-0"
                >
                  {embededVideoUrl && videoThumbnailUrl ? (
                    <AspectRatio
                      ratio={192 / 108}
                      className="relative h-full w-full"
                    >
                      <ClickableDiv
                        className="relative h-full w-full"
                        onClick={() => {
                          showDialog(YoutubeDialog, {
                            url: embededVideoUrl,
                            videoId: getYoutubeVideoId(embededVideoUrl),
                            thumbnailUrl: videoThumbnailUrl,
                          });
                        }}
                      >
                        <Image
                          fill
                          src={videoThumbnailUrl}
                          alt={videoThumbnailUrl}
                          className="h-full w-full object-cover"
                          unoptimized
                        />
                        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                          <Image
                            width={53}
                            height={33}
                            src={YoutubePlayIcon}
                            alt="Play"
                          />
                        </div>
                      </ClickableDiv>
                    </AspectRatio>
                  ) : null}
                </CarouselItem>
              );
            })
          ) : (
            <div className="h-full w-full px-4">
              <Image
                src="/assets/images/wwe-champions-logo-main.webp"
                alt="WWE Champions logo"
                width={192}
                height={120}
                className="h-full w-full object-contain object-center"
              />
            </div>
          )}
        </CarouselContent>
        <DesktopCarouselIndicator />
      </Carousel>
    </AspectRatio>
  );
};

const DesktopCarouselIndicator = () => {
  const { api } = useCarousel();
  const nodes = api?.slideNodes();
  if (!nodes || nodes.length === 1) return null;
  return (
    <div
      className={
        'pointer-events-none absolute inset-x-0 top-[50%] flex h-9 -translate-y-1/2 justify-between'
      }
    >
      <ClickableDiv
        className="pointer-events-auto absolute -left-2 bottom-0 top-0 flex size-[18px] h-9 w-9 cursor-pointer items-center justify-center self-center overflow-hidden rounded-full bg-[#111010] opacity-60"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          api?.scrollPrev();
        }}
      >
        <IcoMoonIcon
          className="size-[10px] rotate-90 text-white"
          iconName="arrow"
        />
      </ClickableDiv>

      <ClickableDiv
        className="pointer-events-auto absolute -right-2 bottom-0 top-0 flex size-[18px] h-9 w-9 cursor-pointer items-center justify-center self-center overflow-hidden rounded-full bg-[#111010] opacity-60"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          api?.scrollNext();
        }}
      >
        <IcoMoonIcon
          className="size-[10px] -rotate-90 text-white"
          iconName="arrow"
        />
      </ClickableDiv>
    </div>
  );
};
